import store from '@/store';
import packApi from '@/apis/pack';
import productApi from '@/apis/product';
import productHelpers from './product';
import { PACK_STATUS_CODE, CONTEXT_TYPE, DOUBLE_X_SKU, AGREEMENT_TYPE } from '../constants';

function createEmptyContext(name = 'composition') {

  const context = {
    name,
    bundle: {
      step1: {
        selectedSkus: [],
        updated: 0,
        expectedTotalPrice: 0,
      },
      step2: {
        finalSkus: [],
        updated: 0,
      },
      step3: {
        packName: '',
        updated: 0,
        paymentType: '',
      },
    },
  };

  return context;
}

function updateStep1(ctx, selectedSkus) {
  ctx.bundle.step1.selectedSkus = selectedSkus;
  ctx.bundle.step1.updated = new Date();

  return ctx;
}

function updateStep2(ctx, finalSkus) {
  ctx.bundle.step2.finalSkus = finalSkus;
  ctx.bundle.step2.updated = new Date();
  return ctx;
}

function updateStep3(ctx, packName, reqNo, subscription, prices) {
  ctx.bundle.step3.packName = packName;
  ctx.bundle.step3.reqNo = reqNo;
  ctx.bundle.step3.subscription = subscription;
  ctx.bundle.step3.prices = prices;
  ctx.bundle.step3.updated = new Date();
  return ctx;
}

function updateStep1Time(ctx) {
  ctx.bundle.step1.updated = new Date();
  return ctx;
}

function updateStep2Time(ctx) {
  ctx.bundle.step2.updated = new Date();
  return ctx;
}

function updateStep3Time(ctx) {
  ctx.bundle.step3.updated = new Date();
  return ctx;
}

function updateContext(store, ctx) {
  store.dispatch('context/set', ctx);
}
function resetContext(store) {
  store.dispatch('context/unsest');
}

function createRecomendationRecompositionContext(store, selectedSkus) {
  const ctx = createEmptyContext(CONTEXT_TYPE.RECOMPOSITION);
  updateStep1(ctx, selectedSkus);
  updateContext(store, ctx);
}

function createRecompositionWithReqNoContext(store, selectedSkus, packName, reqNo = '') {
  const ctx = createEmptyContext(CONTEXT_TYPE.RECOMPOSITION_WITH_REQNO);
  updateStep1(ctx, selectedSkus);
  updateStep3(ctx, packName, reqNo);
  updateContext(store, ctx);
}

function createRecomendationCompositionFinalContext(store, finalSkus, packName, reqNo = '') {
  const ctx = createEmptyContext(CONTEXT_TYPE.COMPOSITION_FINAL);
  updateStep2(ctx, finalSkus);
  updateStep3(ctx, packName, reqNo);
  updateContext(store, ctx);
}

function createReorderCompositionContext(store, finalSkus, packName, subscription, prices, reqNo = '') {
  const ctx = createEmptyContext(CONTEXT_TYPE.COMPOSITION_REORDER);
  updateStep2(ctx, finalSkus);
  updateStep3(ctx, packName, reqNo, subscription, prices);
  updateContext(store, ctx);
}

function createCompositionPaymentContext(store, finalSkus, packName, subscription, prices, reqNo) {
  const ctx = createEmptyContext(CONTEXT_TYPE.COMPOSITION_PAYMENT_WITH_REQNO);
  updateStep2(ctx, finalSkus);
  updateStep3(ctx, packName, reqNo, subscription, prices);
  updateContext(store, ctx);
}

async function doRecompositionFromMypack(store, reqNo) {
  const r = await packApi.getApplianceDetail(reqNo);
  if (r.error) {
    return false;
  }

  const packName = r.order_info[0].mypack_name;
  const selectedSkus = r.order_detail_list.map((n) => {
    const count = n.dose_list
      .split(',')
      .reduce((acc, x) => acc + parseInt(x, 10), 0);
    return {
      item_sku: n.item_sku,
      count: n.item_sku === DOUBLE_X_SKU ? count * 3 : count,
    };
  });

  createRecompositionWithReqNoContext(store, selectedSkus, packName, reqNo);
  return true;
}

async function doReorderFromMypack(store, applianceDetail, subscription, finalSkus, reqNo) {
  const r = applianceDetail;
  const { mypack_name, take_days, gender, age_group, intaker, receive_phone, delivery_address, delivery_message, consulted } = r.order_info[0];
  const doseInfo = finalSkus.map((n) => ({
    item_sku: n.item_sku,
    dose_cnt: n.seq.split(',').reduce((acc, n2) => acc + parseInt(n2, 10), 0),
  }));
  const params = {
    dose_info: doseInfo,
    take_days: take_days,
  };
  const prices = await productApi.getCalculatedPrices(params);

  // payment store
  store.dispatch('payment/reset');

  const paymentInfo = {
    paymentMode: "",
    paymentCode: "",
    customerId: "",
    issuerCode: "",
    issuerName: "",
    installmentPlan: "",
    receiptType: "",
    receiptIssuanceType: "",
    receiptNumberType: "",
    receiptIssuanceNumber: "",
    receivePhone: receive_phone,
    deliveryAddress: delivery_address,
    deliveryMessage: delivery_message,
  };

  const orderPayment = r.order_payment[0];
  if (orderPayment !== undefined) {
    const { payment_mode, payment_code, customer_id, issuer_code, issuer_name, installment_plan,
      receipt_type, receipt_issuance_type, receipt_number_type, receipt_issuance_number } = orderPayment;

    Object.assign(paymentInfo, {
      paymentMode: payment_mode,
      paymentCode: payment_code,
      customerId: customer_id,
      issuerCode: issuer_code,
      issuerName: issuer_name,
      installmentPlan: installment_plan,
      receiptType: receipt_type,
      receiptIssuanceType: receipt_issuance_type,
      receiptNumberType: receipt_number_type,
      receiptIssuanceNumber: receipt_issuance_number,
    });
  }
  store.dispatch('payment/setPaymentInfo', paymentInfo);

  if(consulted) {
    store.dispatch('agreement/init');
    store.dispatch('agreement/updateTargetSex', {
      targetSex: gender,
    });
    store.dispatch('agreement/updateTargetRange', {
      targetRange: age_group,
    });
    store.dispatch('agreement/updateTargetIntaker', {
      targetIntaker: intaker,
    });
  }
  createReorderCompositionContext(store, finalSkus, mypack_name, subscription, prices, reqNo);
}

async function doCompositionPaymentFromMypack(store, reqNo) {
  const r = await packApi.getApplianceDetail(reqNo, true);
  if (r.error) {
    return false;
  }

  const { mypack_name, take_days, gender, age_group, intaker, receive_phone, delivery_address, delivery_message,
    purchase_type, billing_date, billing_status, cfm_kind, abc_cd, consulted
  } = r.order_info[0];

  const subscription = {
    purchaseType: purchase_type,
    abcCd: abc_cd,
    requestType: cfm_kind,
    billingDate: billing_date,
    billingStatus: billing_status,
    isConsultation: consulted,
  };

  console.log("order_detail_list : " + r.order_detail_list);
  const finalSkus = r.order_detail_list.map((n) => ({
    days: take_days,
    item_sku: n.item_sku,
    pockets: n.dose,
    seq: n.dose_list,
  }));
  console.log("finalSkus : " + finalSkus[0]);
  const doseInfo = finalSkus.map((n) => ({
    item_sku: n.item_sku,
    dose_cnt: n.seq.split(',').reduce((acc, n2) => acc + parseInt(n2, 10), 0),
  }));
  const params = {
    dose_info: doseInfo,
    take_days: take_days,
  };
  const prices = await productApi.getCalculatedPrices(params);

  // payment store
  store.dispatch('payment/reset');

  const orderPayment = r.order_payment[0];
  if (orderPayment !== undefined) {
    const { payment_mode, payment_code, customer_id, issuer_code, issuer_name, installment_plan,
      receipt_type, receipt_issuance_type, receipt_number_type, receipt_issuance_number } = r.order_payment[0];

    const paymentInfo = {
      paymentMode: payment_mode,
      paymentCode: payment_code,
      customerId: customer_id,
      issuerCode: issuer_code,
      issuerName: issuer_name,
      installmentPlan: installment_plan,
      receiptType: receipt_type,
      receiptIssuanceType: receipt_issuance_type,
      receiptNumberType: receipt_number_type,
      receiptIssuanceNumber: receipt_issuance_number,
      receivePhone: receive_phone,
      deliveryAddress: delivery_address,
      deliveryMessage: delivery_message,
    };
    store.dispatch('payment/setPaymentInfo', paymentInfo);
  }

  store.dispatch('agreement/init');
  store.dispatch('agreement/updateTargetSex', {
    targetSex: gender,
  });
  store.dispatch('agreement/updateTargetRange', {
    targetRange: age_group,
  });
  store.dispatch('agreement/updateTargetIntaker', {
    targetIntaker: intaker,
  });
  createCompositionPaymentContext(store, finalSkus, mypack_name, subscription, prices, reqNo);
  return true;
}

function doRecompositionFromRecommendationPack(store, recommendationPacks, selectedPackCd) {
  const pack = recommendationPacks.find((n) => n.pack_cd === selectedPackCd);
  const selectedSkus = productHelpers.getSelectedSkusFromPack(pack);
  createRecomendationRecompositionContext(store, selectedSkus);
}

function doCompositionFinalFromRecommendationPack(store, recommendationPacks, selectedPackCd) {
  const pack = recommendationPacks.find((n) => n.pack_cd === selectedPackCd);
  const finalSkus = pack.pack_detail.map((n) => (
    {
      days: pack.take_days,
      item_sku: n.item_sku,
      pockets: pack.take_cnt,
      seq: n.doseSeqs,
    }));
  createRecomendationCompositionFinalContext(store, finalSkus, pack.name);
}

async function checkMypackLimit(refModalAlert) {
  if (!store.state.isLogin) {
    await store.dispatch('ssoLogin');
    await store.dispatch("ssoToken");
  }
  const r = await packApi.checkPackOrderLimit();
  if (r.error) {
    return false;
  }
  if (!r) {
    await refModalAlert
      .openWithMessage('주문서는 최대 12개까지 생성 가능합니다.\n사용하지 않는 주문서 삭제 후 생성 부탁드립니다.');
    return false;
  }
  return true;
}

export default {
  createEmptyContext,
  updateStep1,
  updateStep2,
  updateStep3,
  updateStep1Time,
  updateStep2Time,
  updateStep3Time,
  updateContext,
  resetContext,
  createRecomendationRecompositionContext,
  createRecompositionWithReqNoContext,
  doRecompositionFromMypack,
  doRecompositionFromRecommendationPack,
  doCompositionFinalFromRecommendationPack,
  checkMypackLimit,
  doReorderFromMypack,
  doCompositionPaymentFromMypack,
};
